import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./PropertyDetailsPage.css";

const API_BASE_URL = "/api";
// OR try without port if your backend serves both API and frontend:
// const API_BASE_URL = window.location.origin; // This will use the same origin as frontend

// ✅ FIX: Enhanced axios instance with CORS handling
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
  // Add withCredentials if needed for cookies/auth
  // withCredentials: true,
});

// ✅ FIX: Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log(
      `🚀 Making ${config.method?.toUpperCase()} request to:`,
      config.url
    );
    console.log("Full config:", config);
    return config;
  },
  (error) => {
    console.error("❌ Request error:", error);
    return Promise.reject(error);
  }
);

// ✅ FIX: Add response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`✅ Response from ${response.config.url}:`, response.status);
    return response;
  },
  (error) => {
    console.error("❌ Response error:", {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      url: error.config?.url,
    });
    return Promise.reject(error);
  }
);

export default function PropertyDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [activeTab, setActiveTab] = useState("description"); // "description" or "contact"

  useEffect(() => {
    const fetchPropertyDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log(`🔄 Fetching property details for ID: ${id}`);

        // FIRST, let's check what properties endpoint works
        const testEndpoints = [
          `/api/properties/${id}`, // ✅ CHANGED: Use relative URLs
          `/property/${id}`, // ✅ CHANGED: Use relative URLs
          `/properties/${id}`, // ✅ CHANGED: Use relative URLs
          `/api/property/${id}`, // ✅ CHANGED: Use relative URLs
        ];

        console.log("🔍 Testing endpoints:", testEndpoints);

        let response = null;
        let successfulEndpoint = null;

        for (const endpoint of testEndpoints) {
          try {
            console.log(`Trying: ${endpoint}`);
            response = await api.get(endpoint);

            // Check if response has data
            if (response.data) {
              successfulEndpoint = endpoint;
              console.log(`✅ Success from: ${endpoint}`);
              console.log("📦 Response structure:", response.data);
              break;
            }
          } catch (err) {
            console.log(`❌ Failed: ${endpoint} - ${err.message}`);
            continue;
          }
        }

        if (!response || !response.data) {
          // SECOND APPROACH: Try to get from properties list and filter
          console.log("🔄 Trying to fetch from properties list...");
          try {
            const allProperties = await api.get(
              "/Homeproperties" // ✅ CHANGED: Use relative URL
            );
            console.log("📦 All properties response:", allProperties.data);

            let data = [];
            if (allProperties.data.data) {
              data = allProperties.data.data;
            } else if (allProperties.data.properties) {
              data = allProperties.data.properties;
            } else if (Array.isArray(allProperties.data)) {
              data = allProperties.data;
            }

            console.log(`📊 Total properties fetched: ${data.length}`);

            // Find the property with matching ID
            const foundProperty = data.find(
              (p) =>
                String(p.id) === String(id) ||
                String(p._id) === String(id) ||
                String(p.propertyId) === String(id)
            );

            if (foundProperty) {
              console.log("✅ Found property in list:", foundProperty);
              setProperty(foundProperty);
              return;
            } else {
              console.log(`❌ Property with ID ${id} not found in list`);
              setError(`Property with ID ${id} not found.`);
            }
          } catch (listError) {
            console.error("❌ Error fetching properties list:", listError);
            setError(
              "Could not load property. Please check if the property exists."
            );
          }
        } else {
          // Extract property data from successful endpoint
          const propertyData =
            response.data.data || response.data.property || response.data;

          console.log("📦 Setting property data:", propertyData);
          setProperty(propertyData);
        }
      } catch (error) {
        console.error("❌ Error loading property:", error);
        setError(error.message || "Failed to load property details.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPropertyDetails();
    } else {
      setError("No property ID provided.");
      setLoading(false);
    }
  }, [id]);

  // Debug: Log property data when it changes
  useEffect(() => {
    if (property) {
      console.log("🏠 Current property state:", property);
    }
  }, [property]);

  // Parse images function
  const parseImages = useCallback((propertyData) => {
    if (!propertyData) {
      console.log("⚠️ No property data to parse images from");
      return [];
    }

    const possibleImageFields = [
      "images",
      "image",
      "photos",
      "imageUrl",
      "displayImage",
      "propertyImages",
      "image_url",
      "gallery",
    ];

    console.log("🖼️ Looking for images in property...");

    for (const field of possibleImageFields) {
      if (propertyData[field]) {
        console.log(`✅ Found in '${field}':`, propertyData[field]);

        const imagesData = propertyData[field];

        // If it's a string
        if (typeof imagesData === "string") {
          try {
            // Check if it's JSON
            if (
              imagesData.trim().startsWith("[") ||
              imagesData.trim().startsWith("{")
            ) {
              const parsed = JSON.parse(imagesData);
              if (Array.isArray(parsed)) {
                console.log(
                  `📸 Parsed ${parsed.length} images from JSON string`
                );
                return parsed;
              } else if (parsed && typeof parsed === "object") {
                console.log("📸 Parsed single image object from JSON");
                return [parsed];
              }
            } else {
              // Treat as direct URL
              console.log("📸 Treating as direct URL:", imagesData);
              return [{ FilePath: imagesData }];
            }
          } catch (error) {
            console.error(`❌ Error parsing '${field}':`, error);
            // If parsing fails, treat as direct URL
            return [{ FilePath: imagesData }];
          }
        }
        // If it's an array
        else if (Array.isArray(imagesData)) {
          console.log(`📸 Found ${imagesData.length} images in array`);
          return imagesData;
        }
        // If it's an object (single image)
        else if (typeof imagesData === "object") {
          console.log("📸 Found single image object");
          return [imagesData];
        }
      }
    }

    console.log("⚠️ No images found in any field");
    return [];
  }, []);

  // Get image URL - UPDATED to remove BASE_URL
  // const getImageUrl = useCallback((img) => {
  //   if (!img) {
  //     console.log("⚠️ No image object provided");
  //     return "/placeholder.jpg";
  //   }

  //   // Try different possible field names
  //   const imgPath =
  //     img.FilePath ||
  //     img.filePath ||
  //     img.url ||
  //     img.imageUrl ||
  //     img.src ||
  //     img.image ||
  //     img;

  //   if (!imgPath) {
  //     console.log("⚠️ No image path found in object:", img);
  //     return "/placeholder.jpg";
  //   }

  //   console.log("🖼️ Image path found:", imgPath);

  //   // If already full URL
  //   if (imgPath.startsWith("http")) {
  //     return imgPath;
  //   }

  //   // Handle relative paths - ✅ CHANGED: Remove BASE_URL
  //   let finalUrl;
  //   if (imgPath.startsWith("/")) {
  //     finalUrl = imgPath; // ✅ CHANGED: No BASE_URL prepending
  //   } else {
  //     finalUrl = `/${imgPath}`; // ✅ CHANGED: No BASE_URL prepending
  //   }

  //   console.log("🔗 Final image URL:", finalUrl);
  //   return finalUrl;
  // }, []);
  const getImageUrl = useCallback((img) => {
    if (!img) return "/placeholder.jpg";

    const imgPath =
      img.FilePath ||
      img.filePath ||
      img.url ||
      img.imageUrl ||
      img.src ||
      img.image ||
      img;

    if (!imgPath) return "/placeholder.jpg";

    // Already full URL
    if (imgPath.startsWith("http")) {
      return imgPath;
    }

    // ✅ FIX: Always prepend backend URL
    if (imgPath.startsWith("/")) {
      return `${imgPath}`;
    }

    return `${imgPath}`;
  }, []);

  // Handle image errors
  const handleImageError = (imageUrl, imageIndex) => {
    console.error(`❌ Image ${imageIndex} failed to load:`, imageUrl);
    setImageErrors((prev) => ({
      ...prev,
      [imageIndex]: true,
    }));
  };

  // Get all property data with fallbacks - UPDATED WITH NEW FIELDS
  const propertyData = useMemo(() => {
    if (!property) return null;

    // Parse all the fields you requested
    return {
      // Basic Info
      id: property.id || property._id || property.propertyId || id,
      propertyName:
        property.propertyName || property.name || property.title || "Property",
      price: property.price
        ? `₹ ${parseInt(property.price).toLocaleString("en-IN")}`
        : property.amount || property.cost
        ? `₹ ${parseInt(property.amount || property.cost).toLocaleString(
            "en-IN"
          )}`
        : "Contact for price",
      address:
        property.address || property.fullAddress || "Address not specified",
      size:
        property.size ||
        property.sqft ||
        property.squareFeet ||
        property.area ||
        "N/A",
      propertyType:
        property.propertyType ||
        property.type ||
        property.category ||
        "Property",
      location:
        property.location ||
        property.locality ||
        property.areaName ||
        "Location not specified",
      area:
        property.area ||
        property.locationArea ||
        property.neighborhood ||
        "N/A",
      rentalPeriod:
        property.rentalPeriod ||
        property.leasePeriod ||
        property.tenure ||
        "N/A",

      // Facilities (using boolean or text fields)
      warehouseFacility:
        property.warehouseFacility ||
        property.hasWarehouse ||
        property.warehouse ||
        "Not specified",
      logisticFacility:
        property.logisticFacility ||
        property.hasLogistics ||
        property.logistics ||
        "Not specified",
      highwayConnectivity:
        property.highwayConnectivity ||
        property.nearHighway ||
        property.highwayAccess ||
        "Not specified",
      waterFacility:
        property.waterFacility ||
        property.waterSupply ||
        property.hasWater ||
        "Not specified",
      electricityFacility:
        property.electricityFacility ||
        property.electricity ||
        property.hasElectricity ||
        "Not specified",
      wtpEtp:
        property.wtpEtp ||
        property.hasWTPETP ||
        property.waterTreatment ||
        "Not specified",
      mpcbPermission:
        property.mpcbPermission ||
        property.hasMPCB ||
        property.govPermission ||
        "Not specified",
      nearestLandmark:
        property.nearestLandmark ||
        property.landmark ||
        property.nearbyLandmark ||
        "Not specified",
      featuredCategory:
        property.featuredCategory ||
        property.category ||
        property.featured ||
        "Standard",

      // Contact Info
      ownerName:
        property.ownerName ||
        property.agentName ||
        property.contactName ||
        "Not available",
      ownerPhone:
        property.ownerPhone ||
        property.contactNumber ||
        property.phone ||
        property.mobile ||
        "Not available",

      // Images
      images: parseImages(property),

      // Description (fallback combining multiple fields)
      description:
        property.description ||
        property.details ||
        property.overview ||
        "No description available",

      // Additional fields that might exist
      amenities: property.amenities
        ? Array.isArray(property.amenities)
          ? property.amenities
          : typeof property.amenities === "string"
          ? property.amenities.split(",").map((a) => a.trim())
          : []
        : [],
    };
  }, [property, id, parseImages]);

  // Format boolean values for display
  const formatBooleanValue = (value) => {
    if (
      value === true ||
      value === "true" ||
      value === "yes" ||
      value === "Yes"
    )
      return "Yes";
    if (
      value === false ||
      value === "false" ||
      value === "no" ||
      value === "No"
    )
      return "No";
    return value;
  };

  // Loading component
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading property details...</p>
        <p className="loading-id">Property ID: {id}</p>
      </div>
    );
  }

  // Error component
  if (error) {
    return (
      <div className="error-container">
        <h2>⚠️ Error Loading Property</h2>
        <p className="error-message">{error}</p>
        <div className="error-actions">
          <button onClick={() => navigate(-1)} className="back-button">
            ← Go Back
          </button>
          <button
            onClick={() => navigate("/properties")}
            className="browse-button"
          >
            Browse Properties
          </button>
        </div>
      </div>
    );
  }

  // No property found
  if (!property || !propertyData) {
    return (
      <div className="not-found">
        <h2>Property Not Found</h2>
        <p>The property with ID "{id}" was not found.</p>
        <div className="not-found-actions">
          <button onClick={() => navigate(-1)} className="back-button">
            ← Go Back
          </button>
          <button
            onClick={() => navigate("/properties")}
            className="browse-button"
          >
            Browse All Properties
          </button>
        </div>
      </div>
    );
  }

  const images = propertyData.images;
  console.log(`🖼️ Total images to display: ${images.length}`);

  const sliderSettings = {
    dots: true,
    infinite: images.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: images.length > 1,
    adaptiveHeight: true,
  };

  // Facility items for the description tab
  const facilityItems = [
    { label: "Warehouse Facility", value: propertyData.warehouseFacility },
    { label: "Logistic Facility", value: propertyData.logisticFacility },
    { label: "Highway Connectivity", value: propertyData.highwayConnectivity },
    { label: "Water Facility", value: propertyData.waterFacility },
    { label: "Electricity Facility", value: propertyData.electricityFacility },
    { label: "WTP/ETP", value: propertyData.wtpEtp },
    { label: "MPCB Gov Permission", value: propertyData.mpcbPermission },
    { label: "Nearest Landmark", value: propertyData.nearestLandmark },
    { label: "Featured Category", value: propertyData.featuredCategory },
  ];

  return (
    <div className="property-details-page">
      <div className="property-details-container">
        {/* IMAGE GALLERY */}
        <div className="image-gallery-section">
          {images.length > 0 ? (
            <div className="gallery-container">
              <Slider {...sliderSettings}>
                {images.map((img, index) => {
                  const imageUrl = getImageUrl(img);
                  const hasError = imageErrors[index];

                  return (
                    <div key={index} className="slide-item">
                      {hasError ? (
                        <div className="image-error">
                          <div className="error-icon">📷</div>
                          <p>Image failed to load</p>
                        </div>
                      ) : (
                        <img
                          src={imageUrl}
                          alt={`${propertyData.propertyName} - ${index + 1}`}
                          className="property-detail-image"
                          onError={() => handleImageError(imageUrl, index)}
                          loading="lazy"
                          crossOrigin="anonymous"
                        />
                      )}
                    </div>
                  );
                })}
              </Slider>
              <div className="image-count">
                {images.length} {images.length === 1 ? "image" : "images"}
              </div>
            </div>
          ) : (
            <div className="no-images">
              <div className="no-images-icon">🏠</div>
              <p>No images available</p>
            </div>
          )}
        </div>

        {/* PROPERTY INFO */}
        <div className="property-info-section">
          {/* Property Header */}
          <div className="property-header">
            <h1 className="property-title">{propertyData.propertyName}</h1>
            <div className="property-location">
              <span className="location-icon">📍</span>
              <span>{propertyData.address}</span>
            </div>
            <div className="property-price-tag">{propertyData.price}</div>
            {propertyData.rentalPeriod &&
              propertyData.rentalPeriod !== "N/A" && (
                <div className="rental-period">
                  <span className="period-label">Rental Period: </span>
                  <span className="period-value">
                    {propertyData.rentalPeriod}
                  </span>
                </div>
              )}
          </div>

          {/* Tab Navigation */}
          <div className="tab-navigation">
            <button
              className={`tab-button ${
                activeTab === "description" ? "active" : ""
              }`}
              onClick={() => setActiveTab("description")}
            >
              📋 Property Details
            </button>
            <button
              className={`tab-button ${
                activeTab === "contact" ? "active" : ""
              }`}
              onClick={() => setActiveTab("contact")}
            >
              📞 Contact Info
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === "description" ? (
              /* DESCRIPTION/DETAILS TAB */
              <div className="description-tab">
                {/* Basic Information Grid */}
                <div className="details-grid">
                  <div className="detail-item">
                    <span className="detail-label">Property Name:</span>
                    <span className="detail-value">
                      {propertyData.propertyName}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Price:</span>
                    <span className="detail-value price-highlight">
                      {propertyData.price}
                    </span>
                  </div>
                  <div className="detail-item full-width">
                    <span className="detail-label">Address:</span>
                    <span className="detail-value">{propertyData.address}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Size:</span>
                    <span className="detail-value">
                      {propertyData.size} sqft
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Property Type:</span>
                    <span className="detail-value">
                      {propertyData.propertyType}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Location:</span>
                    <span className="detail-value">
                      {propertyData.location}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Area:</span>
                    <span className="detail-value">{propertyData.area}</span>
                  </div>
                  {propertyData.rentalPeriod &&
                    propertyData.rentalPeriod !== "N/A" && (
                      <div className="detail-item">
                        <span className="detail-label">Rental Period:</span>
                        <span className="detail-value">
                          {propertyData.rentalPeriod}
                        </span>
                      </div>
                    )}
                </div>

                {/* Description Section */}
                {propertyData.description &&
                  propertyData.description !== "No description available" && (
                    <div className="description-section">
                      <h3>Description</h3>
                      <p className="description-text">
                        {propertyData.description}
                      </p>
                    </div>
                  )}

                {/* Facilities Section */}
                <div className="facilities-section">
                  <h3>Facilities & Amenities</h3>
                  <div className="facilities-grid">
                    {facilityItems.map((item, index) => (
                      <div key={index} className="facility-item">
                        <span className="facility-label">{item.label}:</span>
                        <span
                          className={`facility-value ${
                            typeof item.value === "boolean" ||
                            item.value === "Yes" ||
                            item.value === "No"
                              ? item.value === true || item.value === "Yes"
                                ? "available"
                                : "not-available"
                              : ""
                          }`}
                        >
                          {formatBooleanValue(item.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Image Count */}
                <div className="image-info">
                  <div className="images-count">
                    <span className="images-icon">🖼️</span>
                    <span className="images-text">
                      {images.length} {images.length === 1 ? "Image" : "Images"}{" "}
                      Available
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              /* CONTACT TAB */
              <div className="contact-tab">
                <div className="contact-section">
                  <h3>Contact Information</h3>
                  <div className="contact-info">
                    <div className="contact-item">
                      <span className="contact-label">Name:</span>
                      <span className="contact-value">
                        {propertyData.ownerName}
                      </span>
                    </div>
                    <div className="contact-item">
                      <span className="contact-label">Phone:</span>
                      <a
                        href={`tel:${propertyData.ownerPhone}`}
                        className="contact-value phone-link"
                      >
                        {propertyData.ownerPhone}
                      </a>
                    </div>
                  </div>

                  <div className="contact-actions">
                    <a
                      href={`tel:${propertyData.ownerPhone}`}
                      className="call-button"
                    >
                      📞 Call Now
                    </a>
                    <button
                      className="whatsapp-button"
                      onClick={() => {
                        const message = `Hi, I'm interested in ${propertyData.propertyName} (ID: ${propertyData.id})`;
                        const whatsappUrl = `https://wa.me/${propertyData.ownerPhone.replace(
                          /\D/g,
                          ""
                        )}?text=${encodeURIComponent(message)}`;
                        window.open(whatsappUrl, "_blank");
                      }}
                    >
                      💬 WhatsApp
                    </button>
                  </div>

                  <div className="property-id-info">
                    <span className="id-label">Property ID:</span>
                    <span className="id-value">{propertyData.id}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions (Always visible) */}
          <div className="quick-actions">
            <button
              className="action-button back-action"
              onClick={() => navigate(-1)}
            >
              ← Back to Listings
            </button>
            <button
              className="action-button share-action"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert("Link copied to clipboard!");
              }}
            >
              📋 Share Property
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
